import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { trackFormSubmission, trackPhoneClick, trackEmailClick } from '../utils/gtag';

// Mail configuration - for production, use environment variables or backend API
const mailConfig = {
  email: {
    to: 'info@royalrooms.it',
    from: 'noreply@royalrooms.it',
    subject: 'New Contact Form Submission - Royal Rooms B&B',
  },
  custom: {
    endpoint: process.env.REACT_APP_EMAIL_API_ENDPOINT || '', // Set in .env file
    method: 'POST',
  },
};

/**
 * Contact Page Component
 * Features:
 * - Hero section
 * - Two-column layout (form + info + map)
 * - Contact form with validation
 */
const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      alert(
        language === 'it'
          ? 'Per favore, compila tutti i campi obbligatori.'
          : 'Please fill in all required fields.'
      );
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert(
        language === 'it'
          ? 'Inserisci un indirizzo email valido.'
          : 'Please enter a valid email address.'
      );
      return;
    }

    try {
      // Prepare email data
      const emailData = {
        to: mailConfig.email.to,
        from: mailConfig.email.from,
        subject: `${mailConfig.email.subject} - ${formData.subject || 'No Subject'}`,
        name: formData.name,
        email: formData.email,
        message: formData.message,
      };

      // Send email based on configured service
      if (mailConfig.service === 'custom' && mailConfig.custom.endpoint) {
        // Use custom backend endpoint
        const response = await fetch(mailConfig.custom.endpoint, {
          method: mailConfig.custom.method || 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(emailData),
        });

        if (!response.ok) {
          throw new Error('Failed to send email');
        }
      } else {
        // For client-side, use mailto as fallback
        // In production, you should use a backend service
        const mailtoLink = `mailto:${mailConfig.email.to}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`)}`;
        window.location.href = mailtoLink;
      }

      // Track form submission
      trackFormSubmission('contact_form');

      // Success message
      alert(
        language === 'it'
          ? 'Messaggio inviato con successo! Ti risponderemo presto.'
          : 'Message sent successfully! We will get back to you soon.'
      );

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error sending email:', error);
      alert(
        language === 'it'
          ? 'Si è verificato un errore. Per favore, contattaci direttamente via email o telefono.'
          : 'An error occurred. Please contact us directly via email or phone.'
      );
    }
  };

  return (
    <main className="pt-10 sm:pt-14">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center bg-cover bg-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(26, 26, 46, 0.7), rgba(26, 26, 46, 0.7)), url('${process.env.PUBLIC_URL}/inc/images/bg.webp')`,
        }}
      >
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow-lg text-center px-4">
          {t('contact.title')}
        </h1>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-gray-600 mb-12 sm:mb-16 text-base sm:text-lg">
            {t('contact.subtitle')}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-primary-navy font-semibold mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.namePlaceholder')}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-bronze transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-primary-navy font-semibold mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-bronze transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-primary-navy font-semibold mb-2">
                    {t('contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.form.subjectPlaceholder')}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-bronze transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-primary-navy font-semibold mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-primary-bronze transition-colors resize-vertical"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary-bronze text-white py-4 rounded-lg font-bold text-lg shadow-xl hover:bg-[#7a5230] hover:-translate-y-1 transition-all duration-300"
                >
                  {t('contact.form.send')}
                </button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-gray-50 p-6 sm:p-8 rounded-2xl shadow-lg">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl text-primary-bronze">📞</span>
                    <div>
                      <h3 className="text-primary-navy font-bold mb-1">
                        {t('contact.phone')}
                      </h3>
                      <a
                        href="tel:+393278872023"
                        onClick={trackPhoneClick}
                        className="text-gray-700 hover:text-primary-bronze transition-colors"
                      >
                        +39 327 887 2023
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="text-2xl text-primary-bronze">✉️</span>
                    <div>
                      <h3 className="text-primary-navy font-bold mb-1">
                        {t('contact.email')}
                      </h3>
                      <a
                        href="mailto:info@royalrooms.it"
                        onClick={trackEmailClick}
                        className="text-gray-700 hover:text-primary-bronze transition-colors"
                      >
                        info@royalrooms.it
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="text-2xl text-primary-bronze">📍</span>
                    <div>
                      <h3 className="text-primary-navy font-bold mb-1">
                        {t('contact.address')}
                      </h3>
                      <p className="text-gray-700">
                        Piazza Garibaldi, 80<br />
                        Nápoles, Campanha 80142<br />
                        Italy
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden shadow-lg h-[400px] sm:h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3028.1234567890123!2d14.2681!3d40.8522!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDUxJzA3LjkiTiAxNMKwMTYnMDkuMiJF!5e0!3m2!1sen!2sit!4v1234567890123!5m2!1sen!2sit"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Royal Rooms B&B Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;

